import type { TemplateChannel, TemplateContext } from '@fluenceur/types';
import { SUPPORTED_TEMPLATE_PAIRS, isSupportedTemplatePair } from '@fluenceur/types';

/**
 * Channels that form at least one supported (channel, context) pair, in their
 * canonical order. Used to populate the channel selector so the form can never
 * offer a channel with no valid context.
 */
export const supportedChannels = (): TemplateChannel[] => {
  const seen = new Set<TemplateChannel>();
  const channels: TemplateChannel[] = [];
  for (const pair of SUPPORTED_TEMPLATE_PAIRS) {
    if (!seen.has(pair.channel)) {
      seen.add(pair.channel);
      channels.push(pair.channel);
    }
  }
  return channels;
};

/** Contexts that pair with the given channel, in canonical order. */
export const contextsForChannel = (channel: TemplateChannel): TemplateContext[] =>
  SUPPORTED_TEMPLATE_PAIRS.filter((pair) => pair.channel === channel).map((pair) => pair.context);

/** Channels that pair with the given context, in canonical order. */
export const channelsForContext = (context: TemplateContext): TemplateChannel[] =>
  SUPPORTED_TEMPLATE_PAIRS.filter((pair) => pair.context === context).map((pair) => pair.channel);

/**
 * Reconcile a (channel, context) selection so it always names a supported pair.
 * The selector the user just changed is the anchor; the other axis is corrected
 * to its first supported value only when the current combination is invalid.
 *
 * @param channel - the desired channel
 * @param context - the desired context
 * @param anchor - which selector the user changed last and must be preserved
 */
export const reconcilePair = (
  channel: TemplateChannel,
  context: TemplateContext,
  anchor: 'channel' | 'context'
): { channel: TemplateChannel; context: TemplateContext } => {
  if (isSupportedTemplatePair(channel, context)) {
    return { channel, context };
  }

  if (anchor === 'channel') {
    const [firstContext] = contextsForChannel(channel);
    return firstContext ? { channel, context: firstContext } : { channel, context };
  }

  const [firstChannel] = channelsForContext(context);
  return firstChannel ? { channel: firstChannel, context } : { channel, context };
};
